import {
  ArrowDownIcon,
  ArrowUpIcon,
  BorderAllIcon,
  CheckCircledIcon,
  Crosshair2Icon,
  InfoCircledIcon,
  LightningBoltIcon,
} from '@radix-ui/react-icons';
import {
  Badge,
  Box,
  Card,
  Flex,
  Grid,
  Heading,
  IconButton,
  Skeleton,
  Table,
  Text,
} from '@radix-ui/themes';
import { CommonSwitchButton } from 'components/common/buttons/switch-button';
import { AddAveragesIcon } from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTooltip } from 'components/common/tooltip';
import usePitchPerformanceStore from 'components/sections/admin-portal/machines/dialogs/pitch-performance/store/use-pitch-performance-store';
import { getYear } from 'date-fns';
import { format } from 'date-fns-tz';
import { MachinePerformanceHelper } from 'lib_ts/classes/machine-performance.helper';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { LOCAL_TIMEZONE } from 'lib_ts/enums/datetime.enums';
import { BallType } from 'lib_ts/enums/machine.enums';
import {
  MAX_BREAKS_DELTA_IN,
  MAX_BREAKS_DELTA_IN_TRAJEKT_36,
  MAX_PLATE_DELTA_IN,
  MAX_VELO_DELTA_MPH,
} from 'lib_ts/enums/pitch-performance';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { Nullable } from 'lib_ts/interfaces/common/i-generics';
import {
  IGroupedFires,
  IMetricDetails,
  IMetricTally,
  IPitchPerfFilters,
} from 'lib_ts/interfaces/data/i-pitch-performance';
import { IRatioOver } from 'lib_ts/interfaces/i-pitch-performance';
import {
  DEFAULT_PLATE,
  IPlateLoc,
  ITrajektRefBreak,
} from 'lib_ts/interfaces/pitches';
import { IAziAltCoordinate } from 'lib_ts/interfaces/pitches/i-base';
import { first, isNil, mean } from 'lodash';
import { ReactNode, useMemo, useState } from 'react';
import './metrics.scss';

// Icon colors, some RADIX.COLOR values don't WORK for icons?
const VELO_ICON_HEX = '#FFC53D';
const BREAK_ICON_HEX = '#0090FF';
const PLATE_ICON_HEX = '#E5484D';

const formatDate = (start: Date, end: Date) => {
  const formatWithYear = 'MMM d, yyyy';
  const formatWithoutYear = 'MMM d';

  const startYear = getYear(start);
  const endYear = getYear(end);
  const sameYear = startYear === endYear;

  // Dec 15, 2024 - Jan 15, 2025 vs. Nov 15 - Dec 15, 2024
  const startFormat = sameYear ? formatWithoutYear : formatWithYear;
  const endFormat = formatWithYear;

  const startStr = format(start, startFormat, { timeZone: LOCAL_TIMEZONE });
  const endStr = format(end, endFormat, { timeZone: LOCAL_TIMEZONE });

  return `${startStr} - ${endStr}`;
};

const getTooltipDates = (filters: IPitchPerfFilters) => {
  const currRange =
    filters.currPeriod.start && filters.currPeriod.end
      ? formatDate(
          new Date(filters.currPeriod.start),
          new Date(filters.currPeriod.end)
        )
      : '';
  const prevRange =
    filters.prevPeriod.start && filters.prevPeriod.end
      ? formatDate(
          new Date(filters.prevPeriod.start),
          new Date(filters.prevPeriod.end)
        )
      : '';

  return (
    <Flex direction="column" gap="1">
      <Text>Current period: {currRange}</Text>
      <Text>Previous period: {prevRange}</Text>
    </Flex>
  );
};

const DeltaBadge = ({
  delta,
  hide,
  tooltip,
}: {
  delta: Nullable<number>;
  hide?: boolean;
  tooltip?: ReactNode;
}) => {
  if (hide || !delta || isNaN(delta)) {
    return null;
  }

  // skip if we'd show a 0 delta
  const noChange = Math.abs(delta).toFixed(0) === '0';

  if (noChange) {
    return null;
  }

  // green for positive, red for negative
  const badge = (
    <Badge color={delta > 0 ? RADIX.COLOR.SUCCESS : RADIX.COLOR.DANGER}>
      {delta > 0 ? (
        <ArrowUpIcon height="12" width="12" />
      ) : (
        <ArrowDownIcon height="12" width="12" />
      )}
      {Math.abs(delta).toFixed(0)}%
    </Badge>
  );

  if (tooltip) {
    // wrap in a tooltip if necessary
    return <CommonTooltip trigger={badge} content={tooltip} />;
  }

  // return the badge by itself
  return badge;
};

const stDev = (array: number[]) => {
  if (array.length < 2) {
    return 0;
  }

  const avg = mean(array);
  const variance = mean(array.map((x) => Math.pow(x - avg, 2)));
  return Math.sqrt(variance);
};

const ratioOver = <T,>(
  items: T[],
  predicate: (item: T) => boolean
): IRatioOver => {
  const total = items.length;
  const count = items.filter(predicate).length;
  return { count, total, ratio: total === 0 ? 0 : count / total };
};

const RatioOverText = (props: { value: IRatioOver; maxIn: number }) => (
  <Text color={RADIX.COLOR.NEUTRAL}>
    {props.value.count}/{props.value.total} (
    {(props.value.ratio * 100).toFixed(0)}%) &gt; {props.maxIn}&quot;
  </Text>
);

const BreaksHeaderTooltip = (props: {
  isAveraged: boolean;
  groups: IGroupedFires[];
  maxBreakDeltaIn: number;
}) => {
  const errors = useMemo(() => {
    if (props.isAveraged) {
      // one error per group
      const errPerGroup = props.groups.map((g) => {
        const breaks = g.rotatedShots.map((s) =>
          TrajHelper.getBreaksFromShot(s)
        );

        const o: ITrajektRefBreak = {
          xInches: mean(
            breaks.map((b) => Math.abs(b.xInches - g.target.break.xInches))
          ),
          zInches: mean(
            breaks.map((b) => Math.abs(b.zInches - g.target.break.zInches))
          ),
        };

        return o;
      });

      return errPerGroup;
    }

    // one error per shot per group
    const errPerShot = props.groups.flatMap((g) =>
      g.rotatedShots.map((s) => {
        const breaks = TrajHelper.getBreaksFromShot(s);

        const o: ITrajektRefBreak = {
          xInches: Math.abs(breaks.xInches - g.target.break.xInches),
          zInches: Math.abs(breaks.zInches - g.target.break.zInches),
        };

        return o;
      })
    );

    return errPerShot;
  }, [props.isAveraged, props.groups]);

  const xErrOver = useMemo(
    () => ratioOver(errors, (m) => m.xInches > props.maxBreakDeltaIn),
    [errors, props.maxBreakDeltaIn]
  );

  const zErrOver = useMemo(
    () => ratioOver(errors, (m) => m.zInches > props.maxBreakDeltaIn),
    [errors, props.maxBreakDeltaIn]
  );

  // * stdev doesn't care about the isAveraged flag or not because it must be per pitch-group
  const stDevs = useMemo(() => {
    const output: ITrajektRefBreak[] = [];
    props.groups.forEach((g) => {
      const shots = g.rotatedShots;
      if (shots.length < 3) {
        // ignore groups that are too small
        return;
      }

      const breaks = shots.map((s) => TrajHelper.getBreaksFromShot(s));

      const o: ITrajektRefBreak = {
        xInches: stDev(breaks.map((b) => b.xInches)),
        zInches: stDev(breaks.map((b) => b.zInches)),
      };

      output.push(o);
    });

    return output;
  }, [props.groups]);

  const xStDevOver = useMemo(
    () => ratioOver(stDevs, (m) => m.xInches > props.maxBreakDeltaIn),
    [stDevs, props.maxBreakDeltaIn]
  );

  const zStDevOver = useMemo(
    () => ratioOver(stDevs, (m) => m.zInches > props.maxBreakDeltaIn),
    [stDevs, props.maxBreakDeltaIn]
  );

  return (
    <CommonTooltip
      maxWidth="372px"
      noPadding
      content={
        <Table.Root
          className="PitchPerfTooltipTable"
          style={{ width: '372px' }}
        >
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Metric</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>X in</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Z in</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>Break Error</Table.Cell>
              <Table.Cell>
                <Flex direction="column">
                  <Text>
                    {mean(errors.map((m) => m.xInches)).toFixed(2)}&quot;
                  </Text>
                  <RatioOverText
                    value={xErrOver}
                    maxIn={props.maxBreakDeltaIn}
                  />
                </Flex>
              </Table.Cell>
              <Table.Cell>
                <Flex direction="column">
                  <Text>
                    {mean(errors.map((m) => m.zInches)).toFixed(2)}&quot;
                  </Text>
                  <RatioOverText
                    value={zErrOver}
                    maxIn={props.maxBreakDeltaIn}
                  />
                </Flex>
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Break StDev</Table.Cell>
              <Table.Cell>
                <Flex direction="column">
                  <Text>
                    {mean(stDevs.map((m) => m.xInches)).toFixed(2)}&quot;
                  </Text>
                  <RatioOverText
                    value={xStDevOver}
                    maxIn={props.maxBreakDeltaIn}
                  />
                </Flex>
              </Table.Cell>
              <Table.Cell>
                <Flex direction="column">
                  <Text>
                    {mean(stDevs.map((m) => m.zInches)).toFixed(2)}&quot;
                  </Text>
                  <RatioOverText
                    value={zStDevOver}
                    maxIn={props.maxBreakDeltaIn}
                  />
                </Flex>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table.Root>
      }
      trigger={
        <IconButton
          size="1"
          variant={RADIX.BUTTON.VARIANT.SECONDARY}
          color={RADIX.COLOR.NEUTRAL}
        >
          <AddAveragesIcon />
        </IconButton>
      }
    />
  );
};

const PlatesHeaderTooltip = (props: {
  isAveraged: boolean;
  groups: IGroupedFires[];
  maxPlateIn: number;
}) => {
  const errors = useMemo(() => {
    if (props.isAveraged) {
      // one error per group
      const errPerGroup = props.groups.map((g) => {
        const plates = g.rotatedShots.map((s) =>
          TrajHelper.getPlateLoc(s.traj)
        );

        const o: IPlateLoc = {
          plate_x: mean(
            plates.map((b) => Math.abs(b.plate_x - g.target.plate.plate_x))
          ),
          plate_z: mean(
            plates.map((b) => Math.abs(b.plate_z - g.target.plate.plate_z))
          ),
        };

        return o;
      });

      return errPerGroup;
    }

    // one error per shot per group
    const errPerShot = props.groups.flatMap((g) =>
      g.rotatedShots.map((s) => {
        const plate = TrajHelper.getPlateLoc(s.traj);

        const o: IPlateLoc = {
          plate_x: Math.abs(plate.plate_x - g.target.plate.plate_x),
          plate_z: Math.abs(plate.plate_z - g.target.plate.plate_z),
        };

        return o;
      })
    );

    return errPerShot;
  }, [props.isAveraged, props.groups]);

  const xErrOver = useMemo(
    () => ratioOver(errors, (m) => m.plate_x * FT_TO_INCHES > props.maxPlateIn),
    [errors, props.maxPlateIn]
  );

  const zErrOver = useMemo(
    () => ratioOver(errors, (m) => m.plate_z * FT_TO_INCHES > props.maxPlateIn),
    [errors, props.maxPlateIn]
  );

  // * stdev doesn't care about the isAveraged flag or not because it must be per pitch-group
  const stDevs = useMemo(() => {
    // stDev per pitch group w/ multiple shots
    const output: IPlateLoc[] = [];

    props.groups.forEach((g) => {
      const shots = g.rotatedShots;

      if (shots.length < 3) {
        // ignore groups that are too small
        return;
      }

      const refPy = first(shots)?.traj?.py; // rotated shots will all have same py

      if (isNil(refPy)) {
        return;
      }

      const plates = shots.map((s) => TrajHelper.getPlateLoc(s.traj));

      const avgPlate: IPlateLoc = {
        plate_x: mean(plates.map((p) => p.plate_x)),
        plate_z: mean(plates.map((p) => p.plate_z)),
      };

      // Compute the angular correction (altitude = vertical, azimuth = horizontal) that would
      // bring the mean plate location to the center of the strike zone.
      // The negative sign converts "displacement toward center" into a rotation delta.
      // This matches the current algorithm on the Python service.
      const avgDelta: IAziAltCoordinate = {
        altitude_rad: -Math.atan(
          (avgPlate.plate_z - DEFAULT_PLATE.plate_z) / refPy
        ),
        azimuth_rad: -Math.atan(
          (avgPlate.plate_x - DEFAULT_PLATE.plate_x) / refPy
        ),
      };

      // Apply the mean-centering rotation to every shot's trajectory and collect
      // the resulting plate locations.
      const rotPlates: IPlateLoc[] = [];

      for (const shot of shots) {
        if (!shot.traj) {
          continue;
        }

        const rotTraj = TrajHelper.rotateTraj(shot.traj, avgDelta);

        // apply avg tilt and yaw deltas to shot traj
        const rotPlate = TrajHelper.getPlateLoc(rotTraj);
        rotPlates.push(rotPlate);
      }

      const o: IPlateLoc = {
        plate_x: stDev(rotPlates.map((b) => b.plate_x)),
        plate_z: stDev(rotPlates.map((b) => b.plate_z)),
      };

      output.push(o);
    });

    return output;
  }, [props.groups]);

  const xStDevOver = useMemo(
    () => ratioOver(stDevs, (m) => m.plate_x * FT_TO_INCHES > props.maxPlateIn),
    [stDevs, props.maxPlateIn]
  );

  const zStDevOver = useMemo(
    () => ratioOver(stDevs, (m) => m.plate_z * FT_TO_INCHES > props.maxPlateIn),
    [stDevs, props.maxPlateIn]
  );

  return (
    <CommonTooltip
      maxWidth="372px"
      noPadding
      content={
        <Table.Root
          className="PitchPerfTooltipTable"
          style={{ width: '372px' }}
        >
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Metric</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>X in</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Z in</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            <Table.Row>
              <Table.Cell>Plate Error</Table.Cell>
              <Table.Cell>
                <Flex direction="column">
                  <Text>
                    {mean(errors.map((m) => m.plate_x * FT_TO_INCHES)).toFixed(
                      2
                    )}
                  </Text>
                  <RatioOverText value={xErrOver} maxIn={props.maxPlateIn} />
                </Flex>
              </Table.Cell>
              <Table.Cell>
                <Flex direction="column">
                  <Text>
                    {mean(errors.map((m) => m.plate_z * FT_TO_INCHES)).toFixed(
                      2
                    )}
                  </Text>
                  <RatioOverText value={zErrOver} maxIn={props.maxPlateIn} />
                </Flex>
              </Table.Cell>
            </Table.Row>
            <Table.Row>
              <Table.Cell>Plate StDev</Table.Cell>
              <Table.Cell>
                <Flex direction="column">
                  <Text>
                    {mean(stDevs.map((m) => m.plate_x * FT_TO_INCHES)).toFixed(
                      2
                    )}
                  </Text>
                  <RatioOverText value={xStDevOver} maxIn={props.maxPlateIn} />
                </Flex>
              </Table.Cell>
              <Table.Cell>
                <Flex direction="column">
                  <Text>
                    {mean(stDevs.map((m) => m.plate_z * FT_TO_INCHES)).toFixed(
                      2
                    )}
                  </Text>
                  <RatioOverText value={zStDevOver} maxIn={props.maxPlateIn} />
                </Flex>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table.Root>
      }
      trigger={
        <IconButton
          size="1"
          variant={RADIX.BUTTON.VARIANT.SECONDARY}
          color={RADIX.COLOR.NEUTRAL}
        >
          <AddAveragesIcon />
        </IconButton>
      }
    />
  );
};

export const PitchPerfMetrics = (props: {
  groups: IGroupedFires[];
  refGroups: IGroupedFires[];
  loading: boolean;
}) => {
  const selectedShotIds = usePitchPerformanceStore((s) => s.selectedShotIds);
  const filters = usePitchPerformanceStore((s) => s.filters);

  const [isAveraged, setIsAveraged] = useState(true);

  const metrics = useMemo(() => {
    let current: IMetricTally;
    let previous: IMetricTally;

    const groups = props.groups.filter((g) => g.isCurrentMs);
    const refGroups = props.refGroups.filter((g) => g.isCurrentMs);

    if (selectedShotIds.length > 0) {
      // We're showing the Shot table and the user has selected shots. Only show metrics for those shots.
      current = MachinePerformanceHelper.getTallyFromShotIds(
        groups,
        selectedShotIds
      );
      previous = MachinePerformanceHelper.getTallyFromShotIds(
        refGroups,
        selectedShotIds
      );
    } else {
      current = isAveraged
        ? MachinePerformanceHelper.getAverageTallyFromGroups(groups)
        : MachinePerformanceHelper.getTallyFromGroups(groups);

      previous = isAveraged
        ? MachinePerformanceHelper.getAverageTallyFromGroups(refGroups)
        : MachinePerformanceHelper.getTallyFromGroups(refGroups);
    }

    const deltas: IMetricDetails | undefined =
      current.counts.total === 0
        ? undefined
        : {
            total:
              (100 * (current.counts.total - previous.counts.total)) /
              current.counts.total,
            overall: current.percents.overall - previous.percents.overall,
            velocity: current.percents.velocity - previous.percents.velocity,
            break: current.percents.break - previous.percents.break,
            plate: current.percents.plate - previous.percents.plate,
          };

    return {
      current: current,
      previous: previous,
      deltas: deltas,
    };
  }, [props.groups, props.refGroups, isAveraged, selectedShotIds]);

  const { current, deltas } = metrics;

  const metricTarget = isAveraged ? 'Pitches' : 'Shots';

  const MAX_BREAKS_DELTA_INCHES =
    filters.ball_type === BallType.Trajekt_36
      ? MAX_BREAKS_DELTA_IN_TRAJEKT_36
      : MAX_BREAKS_DELTA_IN;

  const hideDeltas = !!selectedShotIds.length;

  const tooltipContent = getTooltipDates(filters);

  return (
    <ErrorBoundary identifier="PitchPerfMetrics">
      <Flex direction="column" gap="3">
        <Flex align="center" gap="2">
          <Heading size="4">Performance Metrics</Heading>
          <CommonTooltip
            content={tooltipContent}
            trigger={<InfoCircledIcon cursor="pointer" />}
          />

          <Box flexGrow="1">{/* placeholder */}</Box>

          <Box>
            <CommonSwitchButton
              id="toggle-averages"
              switchPosition="right"
              variant="soft"
              size="1"
              displayTrue="Averages"
              displayFalse="Averages"
              value={isAveraged}
              onChange={(v) => setIsAveraged(v)}
            />
          </Box>
        </Flex>

        <Grid columns={{ xs: '1', sm: '2', lg: '4' }} gap="4">
          <Skeleton loading={props.loading}>
            <Card size="2">
              <Heading size="3">Good {metricTarget}</Heading>
              <Flex mt="1">
                <Text size="1" truncate>
                  {metricTarget} acceptable across all metrics
                </Text>
              </Flex>

              {current && (
                <Flex align="center" gap={RADIX.FLEX.GAP.SM} mt="2">
                  <CheckCircledIcon
                    color={RADIX.COLOR.SUCCESS}
                    height="16"
                    width="16"
                  />
                  <Heading size="6">
                    {current.percents.overall.toFixed(0)}%
                  </Heading>
                  <DeltaBadge
                    delta={deltas?.overall ?? null}
                    hide={hideDeltas}
                    tooltip={tooltipContent}
                  />
                </Flex>
              )}
            </Card>
          </Skeleton>
          <Skeleton loading={props.loading}>
            <Card size="2">
              <Heading size="3">Velocity</Heading>
              <Flex mt="1">
                <Text size="1" truncate>
                  {metricTarget} within {MAX_VELO_DELTA_MPH}mph of target
                </Text>
              </Flex>

              {current && (
                <Flex align="center" gap={RADIX.FLEX.GAP.SM} mt="2">
                  {/* I have no idea why, but RADIX.COLOR.WARNING does not work for icons? */}
                  <LightningBoltIcon
                    color={VELO_ICON_HEX}
                    height="16"
                    width="16"
                  />
                  <Heading size="6">
                    {current.percents.velocity.toFixed(0)}%
                  </Heading>
                  <DeltaBadge
                    delta={deltas?.velocity ?? null}
                    hide={hideDeltas}
                    tooltip={tooltipContent}
                  />
                </Flex>
              )}
            </Card>
          </Skeleton>
          <Skeleton loading={props.loading}>
            <Card size="2">
              <Flex justify="between">
                <Heading size="3">Break</Heading>
                <BreaksHeaderTooltip
                  isAveraged={isAveraged}
                  groups={props.groups}
                  maxBreakDeltaIn={MAX_BREAKS_DELTA_INCHES}
                />
              </Flex>

              <Flex mt="1">
                <Text size="1" truncate>
                  {metricTarget} within {MAX_BREAKS_DELTA_INCHES}" of target
                </Text>
              </Flex>

              {current && (
                <Flex align="center" gap={RADIX.FLEX.GAP.SM} mt="2">
                  <BorderAllIcon
                    color={BREAK_ICON_HEX}
                    height="16"
                    width="16"
                  />
                  <Heading size="6">
                    {current.percents.break.toFixed(0)}%
                  </Heading>
                  <DeltaBadge
                    delta={deltas?.break ?? null}
                    hide={hideDeltas}
                    tooltip={tooltipContent}
                  />
                </Flex>
              )}
            </Card>
          </Skeleton>
          <Skeleton loading={props.loading}>
            <Card size="2">
              <Flex justify="between">
                <Heading size="3">Plate Location</Heading>
                <PlatesHeaderTooltip
                  isAveraged={isAveraged}
                  groups={props.groups}
                  maxPlateIn={MAX_PLATE_DELTA_IN}
                />
              </Flex>

              <Flex mt="1">
                <Text size="1" truncate>
                  {metricTarget} within {MAX_PLATE_DELTA_IN}" of target
                </Text>
              </Flex>

              {current && (
                <Flex align="center" gap={RADIX.FLEX.GAP.SM} mt="2">
                  <Crosshair2Icon
                    color={PLATE_ICON_HEX}
                    height="16"
                    width="16"
                  />
                  <Heading size="6">
                    {current.percents.plate.toFixed(0)}%
                  </Heading>
                  <DeltaBadge
                    delta={deltas?.plate ?? null}
                    hide={hideDeltas}
                    tooltip={tooltipContent}
                  />
                </Flex>
              )}
            </Card>
          </Skeleton>
        </Grid>
      </Flex>
    </ErrorBoundary>
  );
};
